import React from "react";
import { Link } from "react-router-dom";
import { Loader2, FileText } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";

const CAD = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

/**
 * Phase 19.10 refactor — extracted from EmployeeDetail.jsx.
 * Pure presentation component: just renders a list of pay stubs for one employee
 * given the parent's lazy-loaded state.
 */
export const EmployeePayStubsTab = ({ payStubs, payStubsLoaded }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <FileText className="h-5 w-5" />
        Pay Stubs
      </CardTitle>
      <CardDescription>
        Real pay stubs from approved &amp; paid payroll runs. New stubs appear
        automatically once a payroll run is approved.{" "}
        <Link to="/pay-stubs" className="text-primary hover:underline">
          Open full Pay Stubs view →
        </Link>
      </CardDescription>
    </CardHeader>
    <CardContent>
      {!payStubsLoaded ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : payStubs.length === 0 ? (
        <div
          className="text-center py-10 text-muted-foreground"
          data-testid="paystubs-empty"
        >
          <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No pay stubs yet for this employee</p>
          <p className="text-xs mt-1">
            Stubs appear once payroll runs are approved.
          </p>
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Pay Date</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payStubs.map((s) => (
                <TableRow key={s.id} data-testid={`emp-paystub-${s.id}`}>
                  <TableCell className="text-sm">
                    {s.period_start} – {s.period_end}
                  </TableCell>
                  <TableCell className="text-sm">
                    {s.pay_date || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {(s.regular_hours || 0).toFixed(1)}
                    {s.overtime_hours
                      ? ` (+${s.overtime_hours.toFixed(1)} OT)`
                      : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    {CAD.format(s.gross_pay || 0)}
                  </TableCell>
                  <TableCell className="text-right text-red-600">
                    -{CAD.format(s.total_deductions || 0)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-emerald-700">
                    {CAD.format(s.net_pay || 0)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        s.status === "paid"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"
                      }
                    >
                      {s.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

export default EmployeePayStubsTab;
